import { observable, action, computed } from 'mobx';
import { RegistrySearchFlags } from '../enums';
import { TreeViewItem } from '../component/treeView';
import { registryEditorApi } from '../api/registry-editor';
import { when } from '../util';

const RecordsPerPage = 50;

export class RegistryEditorStore {
  @observable currentAddressText: string = '';

  @observable inputItemData: string = '';

  @observable inputItemName: string = '';

  @observable isLoading: boolean;

  @observable isSearchPaneHidden: boolean = true;
  @observable isSearchPaneOpen: boolean = false;

  @observable
  registryKeys: Record<string, RegistryKeyTreeItem> = {} as Record<
    string,
    RegistryKeyTreeItem
  >;

  @observable rootKeys: RegistryKeyTreeItem[] = [];

  @observable
  searchFlags: RegistrySearchFlags = RegistrySearchFlags.Keys |
  RegistrySearchFlags.Values |
  RegistrySearchFlags.Data;

  @observable searchMatches: RegistrySearchMatch[] = [];
  @observable searchMatchesDisplayed: RegistrySearchMatch[] = [];

  @observable searchPanePage: number = 1;
  @observable searchPaneDisplayPage: number = 1;

  @computed
  get searchPaneTotalPages() {
    return Math.ceil((this.searchMatches || []).length / RecordsPerPage) || 1;
  }

  @observable searchStatus: RegistrySearchStatus;

  @observable searchString: string = '';

  @observable selectedNode: RegistryKeyTreeItem;

  @observable selectedValue: RegistryValue;

  @action
  addKeyTree(keyNode: RegistryKeyNode) {
    if (!keyNode) {
      console.log('Registry key node missing.');
      return;
    }
    var treeItem =
      this.registryKeys[keyNode.fullKeyName] ||
      new RegistryKeyTreeItem(keyNode);

    if (this.selectedNode) {
      this.selectedNode.isSelected = false;
    }

    treeItem.values = keyNode.values;
    this.selectedNode = treeItem;
    treeItem.isSelected = true;
    treeItem.isExpanded = true;
    this.currentAddressText = treeItem.fullKeyName;

    treeItem.childKeyNodes = [];

    if (keyNode.childKeyNodes) {
      this.createChildren(keyNode.childKeyNodes, treeItem);
    }

    this.registryKeys[keyNode.fullKeyName] = treeItem;

    this.createParentTreeItems(keyNode);
  }

  @action
  addRootKeys(rootKeys: RegistryKeyNode[]) {
    if (rootKeys) {
      this.rootKeys = [];
      rootKeys.forEach(node => {
        this.registryKeys[node.fullKeyName] = new RegistryKeyTreeItem(node);
        this.rootKeys.push(this.registryKeys[node.fullKeyName]);
      });
    }
  }

  @action
  addSearchMatch(match: RegistrySearchMatch) {
    if (match) {
      this.searchMatches.push(match);

      var totalMatches = this.searchMatches.length;
      if (
        totalMatches > (this.searchPanePage - 1) * RecordsPerPage &&
        totalMatches <= this.searchPanePage * RecordsPerPage
      ) {
        this.searchMatchesDisplayed.push(match);
      }
    }
  }

  @action
  addSubKey(parentKeyPath: string, subkey: RegistryKeyNode) {
    if (!subkey) {
      console.error('Subkey not specified.');
      return;
    }

    var parentItem = this.registryKeys[parentKeyPath];
    if (!parentItem) {
      console.error('Parent key not found: ', parentKeyPath);
      return;
    }

    var newSubKey = this.createChildItem(subkey, parentItem);

    parentItem.childKeyNodes = parentItem.childKeyNodes.slice().sort((a, b) => {
      return String(a.displayName).localeCompare(
        String(b.displayName),
        undefined,
        {
          usage: 'sort',
          sensitivity: 'base',
        },
      );
    });

    parentItem.isExpanded = true;
    parentItem.hasChildren = true;
    this.setSelectedNode(newSubKey);
  }

  @action
  addSubKeys(parentKeyPath: string, subKeys: RegistryKeyNode[]) {
    var parentItem = this.registryKeys[parentKeyPath];
    if (!parentItem) {
      console.error('Parent key not found.');
      return;
    }

    if ((subKeys || []).length == 0) {
      parentItem.isExpanded = false;
      parentItem.hasSubKeys = false;
      parentItem.hasChildren = false;
      parentItem.childKeyNodes = null;
      return;
    }

    parentItem.hasSubKeys = true;

    parentItem.childKeyNodes = [];
    this.createChildren(subKeys, parentItem);
  }

  @action
  clearSearchMatches() {
    this.searchMatches = [];
    this.searchMatchesDisplayed = [];
  }

  hasSearchFlags(searchFlags: RegistrySearchFlags) {
    return searchFlags == (this.searchFlags & searchFlags);
  }

  @action
  setAddressText(val: string) {
    this.currentAddressText = val;
  }

  @action
  setKeyValues(registryKey: RegistryKeyNode) {
    var storedKey = this.registryKeys[registryKey.fullKeyName];
    if (storedKey) {
      storedKey.values = registryKey.values;

      // If a value is already selected, and it exists in the new
      // new values being set for this key, select it again.
      if (this.selectedValue && storedKey.values) {
        var matchedValue = storedKey.values.find(
          x =>
            x.keyName + x.name ==
            this.selectedValue.keyName + this.selectedValue.name,
        );
        if (matchedValue) {
          this.selectedValue = matchedValue;
        }
      }
    }
  }

  scrollToNode(fullKeyPath: string) {
    when(() => {
      return (
        !!this.registryKeys[fullKeyPath] &&
        !!this.registryKeys[fullKeyPath].domRef &&
        !!this.registryKeys[fullKeyPath].domRef.current
      );
    })
      .then(() => {
        var keyNode = this.registryKeys[fullKeyPath];

        if (keyNode) {
          if (!keyNode.domRef || !keyNode.domRef.current) {
            console.warn('DOM ref not found for key node.  Unable to scroll.');
            return;
          }
          keyNode.domRef.current.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.warn('Key not found. Unable to scroll.');
        }
      })
      .catch(reason => console.log(reason));
  }

  @action
  setInputItemName(val: string) {
    this.inputItemName = val;
  }

  @action
  setInputItemData(val: object | string) {
    if (val instanceof Array) {
      this.inputItemData = (val as Array<string>)
        .map(x => String(x))
        .join('\n');
    } else {
      this.inputItemData = String(val);
    }
  }

  @action
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @action
  setIsSearchPaneOpen(isOpen: boolean) {
    this.isSearchPaneOpen = isOpen;
  }

  @action
  setIsSearchPaneHidden(isHidden: boolean) {
    this.isSearchPaneHidden = isHidden;
  }

  @action
  setSearchFlags(searchFlags: RegistrySearchFlags) {
    this.searchFlags = searchFlags;
  }

  @action
  setSearchPaneDisplayPage(page: number) {
    this.searchPaneDisplayPage = page;
  }

  @action
  setSearchPanePage = (page: number) => {
    this.searchPanePage = page;
    this.searchMatchesDisplayed = this.searchMatches.slice(
      (page - 1) * RecordsPerPage,
      page * RecordsPerPage,
    );
  };

  @action
  setSearchStatus(searchStatus: RegistrySearchStatus) {
    this.searchStatus = searchStatus;
  }

  @action
  setSearchString(searchString: string) {
    this.searchString = searchString;
  }

  @action
  setSelectedNode(item: RegistryKeyTreeItem) {
    if (this.selectedNode) {
      // This node is already selected.  Abort.
      if (this.selectedNode == item) {
        return;
      }

      // De-select current node.
      this.selectedNode.isSelected = false;
    }

    this.selectedNode = item;
    this.selectedValue = null;

    // Allow null to be passed in.
    if (item) {
      item.isSelected = true;
      this.currentAddressText = item.fullKeyName;
      registryEditorApi.getValues(item.fullKeyName);
    }
  }

  @action
  setSelectedValue(item: RegistryValue) {
    if (this.selectedValue && item == this.selectedValue) {
      // Value already selected.  Abort.
      return;
    }

    this.selectedValue = item;
  }

  @action
  toggleKeyExpansion(item: RegistryKeyTreeItem) {
    // If it's going to be expanded, clear the child
    // keys first so we don't render them twice.
    if (!item.isExpanded) {
      item.childKeyNodes = [];
    }

    item.isExpanded = !item.isExpanded;

    if (item.isExpanded) {
      registryEditorApi.getSubkeys(item.fullKeyName);
    }
  }

  @action
  private createChildItem(
    child: RegistryKeyNode,
    parent: RegistryKeyTreeItem,
  ): RegistryKeyTreeItem {
    var childItem = new RegistryKeyTreeItem(child);
    this.registryKeys[childItem.fullKeyName] = childItem;
    childItem.parentKeyNode = parent;
    parent.childKeyNodes.push(childItem);
    return childItem;
  }

  @action
  private createChildren(
    subKeys: RegistryKeyNode[],
    parentItem: RegistryKeyTreeItem,
  ) {
    var childrenTreeItems = new Array<RegistryKeyTreeItem>();
    subKeys.forEach(x => {
      var childItem = new RegistryKeyTreeItem(x);
      this.registryKeys[childItem.fullKeyName] = childItem;
      childrenTreeItems.push(childItem);
      childItem.parentKeyNode = parentItem;
    });
    parentItem.childKeyNodes = childrenTreeItems;
  }

  @action
  private createParentTreeItems(targetKey: RegistryKeyNode) {
    if (!targetKey.parentKeyNode) {
      return;
    }

    var childTreeItem = this.registryKeys[targetKey.fullKeyName];

    var parentItem =
      this.registryKeys[targetKey.parentKeyNode.fullKeyName] ||
      new RegistryKeyTreeItem(targetKey.parentKeyNode);

    childTreeItem.parentKeyNode = parentItem;

    parentItem.isExpanded = true;

    parentItem.childKeyNodes = [childTreeItem];

    targetKey.parentKeyNode.childKeyNodes
      .filter(x => x.fullKeyName != childTreeItem.fullKeyName)
      .forEach(x => {
        this.createChildItem(x, parentItem);
      });

    // Mobx won't trigger a state change unless slice is used to create
    // a copy of the array that's being set.
    parentItem.childKeyNodes = parentItem.childKeyNodes.slice().sort((a, b) => {
      return String(a.displayName).localeCompare(
        String(b.displayName),
        undefined,
        {
          usage: 'sort',
          sensitivity: 'base',
        },
      );
    });

    this.registryKeys[parentItem.fullKeyName] = parentItem;

    this.createParentTreeItems(targetKey.parentKeyNode);
  }
}

export class RegistryKeyTreeItem implements TreeViewItem {
  constructor(keyNode: RegistryKeyNode) {
    this.displayName = keyNode.displayName;
    this.fullKeyName = keyNode.fullKeyName;
    this.key = keyNode.fullKeyName;
    this.hasSubKeys = keyNode.hasSubKeys;
    this.hasChildren = keyNode.hasSubKeys;
    this.hasValues = keyNode.hasValues;
    this.isExpanded = false;
    this.isSelected = false;
    this.values = keyNode.values;
  }

  key: string;
  @observable domRef: React.RefObject<HTMLDivElement>;
  @observable displayName: string;
  @observable fullKeyName: string;
  @observable hasChildren: boolean;
  @observable hasSubKeys: boolean;
  @observable hasValues: boolean;
  @observable isExpanded: boolean;
  @observable isSelected: boolean;
  @observable values: RegistryValue[];
  @observable childKeyNodes: RegistryKeyTreeItem[] = [];
  @observable parentKeyNode: RegistryKeyTreeItem;
}

export const registryEditorStore = new RegistryEditorStore();
